import { defineStore } from 'pinia'
import { ref } from 'vue'
import { apiDelete, apiGet, apiPost, apiPut } from '@/lib/api'

export interface DesignTheme {
  id: string
  name: string
  description?: string
  tokens: Record<string, string>
  css?: string
  template: boolean
  owner_id?: string
  created_at: string
  updated_at: string
}

export type DesignThemeScope = 'root' | 'page'

const tokenNames = new WeakMap<HTMLElement, string[]>()

const styles = new Map<DesignThemeScope, HTMLStyleElement>()

const putTokens = (el: HTMLElement, tokens: Record<string, string>) => {
  for (const name of tokenNames.get(el) ?? []) el.style.removeProperty(name)
  for (const [name, value] of Object.entries(tokens)) el.style.setProperty(name, value)
  tokenNames.set(el, Object.keys(tokens))
}

const PAGE_ROOT = 'data-hvergel-page'

const putCss = (scope: DesignThemeScope, css: string) => {
  const held = styles.get(scope)
  if (!css) {
    held?.remove()
    styles.delete(scope)
    return
  }
  const el = held ?? document.createElement('style')
  el.textContent = scope === 'page' ? `@scope ([${PAGE_ROOT}]) {\n${css}\n}` : css
  if (!held) {
    el.dataset.hvergelTheme = scope
    document.head.appendChild(el)
    styles.set(scope, el)
  }
}

export const useThemesStore = defineStore('themes', () => {
  const themes = ref<DesignTheme[]>([])
  const applied = ref<DesignTheme | null>(null)

  const getThemes = async (): Promise<DesignTheme[]> => {
    themes.value = await apiGet<DesignTheme[]>('/themes')
    return themes.value
  }

  const getTheme = (id: string) => apiGet<DesignTheme>(`/themes/${id}`)

  const postTheme = async (input: Partial<DesignTheme>): Promise<DesignTheme> => {
    const created = await apiPost<DesignTheme>('/themes', input)
    themes.value = [...themes.value, created]
    return created
  }

  const putTheme = async (theme: DesignTheme): Promise<DesignTheme> => {
    const updated = await apiPut<DesignTheme>(`/themes/${theme.id}`, theme)
    themes.value = themes.value.map((t) => (t.id === updated.id ? updated : t))
    return updated
  }

  const deleteTheme = async (id: string): Promise<void> => {
    await apiDelete<void>(`/themes/${id}`)
    themes.value = themes.value.filter((t) => t.id !== id)
  }

  const getAppliedTheme = (pageId?: string) => apiGet<DesignTheme>(`/themes/applied${pageId ? `?page=${pageId}` : ''}`)

  const putRootTheme = async (): Promise<DesignTheme> => {
    const theme = await getAppliedTheme()
    applied.value = theme
    putTokens(document.documentElement, theme.tokens)
    putCss('root', theme.css ?? '')
    return theme
  }

  const putPageTheme = async (el: HTMLElement, pageId: string): Promise<DesignTheme> => {
    const theme = await getAppliedTheme(pageId)
    el.setAttribute(PAGE_ROOT, pageId)
    putTokens(el, theme.tokens)
    putCss('page', theme.id === applied.value?.id ? '' : theme.css ?? '')
    return theme
  }

  const deletePageTheme = (el: HTMLElement) => {
    el.removeAttribute(PAGE_ROOT)
    putTokens(el, {})
    putCss('page', '')
  }

  return { themes, applied, getThemes, getTheme, postTheme, putTheme, deleteTheme, getAppliedTheme, putRootTheme, putPageTheme, deletePageTheme }
})
